/**
 * localMetricsKpis.ts — KPIs locales del Dashboard y tarjetas de resumen.
 *
 * QUÉ HACE: calcula stats de hoy (ventas, stock bajo, empleados), la serie de
 *   los últimos 7 días con la comparativa vs ayer para la tarjeta "Ventas Hoy",
 *   extras de KPI (ticket promedio, hora pico, producto estrella, margen
 *   estimado), el rango de años del negocio, la meta del día y las tareas
 *   pendientes desde LS.
 *
 * QUÉ INVOCA: txRead (transactionalStore), hasUserRealCajaActivity (caja),
 *   computeLocalPeakHours (series), computeLocalTopProducts y
 *   computeLocalTeamPerformance (products), helpers de localMetricsShared.
 *
 * QUÉ EXPORTA: ChartYearRange, computeLocalDashboardStats,
 *   computeVentasTiendaHoy, computeLocalDashboardKpiExtras, hasLocalVentasData,
 *   computeChartYearRange, computeVentasYesterday, computeMetaDia,
 *   computeLocalTareasPendientes.
 *
 * QUÉ NO DEBE HACER: UI ni HTTP.
 */
import { txRead } from "@/lib/persistence/transactionalStore";
import { defaultProducts } from "@/mocks/data/products";
import { hasUserRealCajaActivity } from "@/features/caja/services/caja.service";
import {
  format,
  subDays,
  addDays,
  startOfDay,
  startOfMonth,
  endOfDay,
  isSameDay,
  getHours,
} from "date-fns";
import type {
  DashboardStats,
  DashboardKpiExtras,
  SalesPoint,
} from "../types/metrics.types";
import {
  pedidoFecha,
  pedidosActivos,
  readPedidosLocal,
  readVentasLocal,
  ventaFecha,
  ventasActivas,
  WEEKDAY_SHORT,
  weekdayIndex,
  type PedidoLocal,
  type VentaLocal,
} from "./localMetricsShared";
import {
  computeLocalPeakHours,
  computeLocalMonthlyTransactions,
  type LocalPeakHourPoint,
} from "./localMetricsSeries";
import {
  computeLocalTeamPerformance,
  computeLocalTopProducts,
} from "./localMetricsProducts";

/** Rango de años con datos del negocio (controla décadas/años del gráfico). */
export interface ChartYearRange {
  minYear: number;
  maxYear: number;
}

/**
 * Serie de los últimos 7 días (más antiguo → hoy) con el desglose Caja/Web por
 * día, igual que `SalesPoint` (`total` = caja, `tienda` = web). El último punto
 * es hoy y se rotula "Hoy"; los demás usan el día corto (Lun…Dom). Solo cuenta
 * pedidos web con fecha de creación válida, para que el día de un pedido no se
 * desplace por un dato incompleto.
 */
function buildLast7DaysSeries(
  ventas: VentaLocal[],
  pedidos: PedidoLocal[],
  hoy: Date,
): SalesPoint[] {
  return Array.from({ length: 7 }, (_, index) => {
    const dia = subDays(hoy, 6 - index);
    const ventasDia = ventas.filter((v) => isSameDay(ventaFecha(v), dia));
    const pedidosDia = pedidos.filter((p) => isSameDay(pedidoFecha(p), dia));
    return {
      name: index === 6 ? "Hoy" : WEEKDAY_SHORT[weekdayIndex(dia)],
      total: ventasDia.reduce((sum, v) => sum + (v.total || 0), 0),
      tienda: pedidosDia.reduce((sum, p) => sum + (p.total || 0), 0),
      cantidad: ventasDia.length + pedidosDia.length,
      pedidos: pedidosDia.length,
    };
  });
}

/**
 * Factores deterministas (más antiguo → ayer) para la serie demo. El seed de
 * caja fecha todas sus ventas en "hoy", así que los 6 días previos quedarían en
 * $0 y la mini-gráfica de "Ventas Hoy" se vería plana. En modo demo (sin
 * actividad POS real) se rellenan con montos variados y realistas (≈55 %–95 %
 * del total de hoy) para que el eje X muestre una tendencia real.
 */
const DEMO_PREVIOUS_DAY_FACTORS = [0.62, 0.81, 0.57, 0.93, 0.71, 0.86];

/** Rellena con montos demo los días previos que no tienen ventas (solo demo). */
function fillDemoPreviousDays(series: SalesPoint[]): SalesPoint[] {
  const last = series[series.length - 1];
  const todayTotal = (last?.total ?? 0) + (last?.tienda ?? 0);
  if (todayTotal <= 0) return series;
  return series.map((point, index) => {
    const isToday = index === series.length - 1;
    const hasData = (point.total ?? 0) + (point.tienda ?? 0) > 0;
    if (isToday || hasData) return point;
    const factor =
      DEMO_PREVIOUS_DAY_FACTORS[index % DEMO_PREVIOUS_DAY_FACTORS.length];
    const dayTotal = Math.round((todayTotal * factor) / 1000) * 1000;
    const webAmount = Math.round(dayTotal * 0.2);
    return {
      ...point,
      total: Math.round(dayTotal * 0.8),
      tienda: webAmount,
      cantidad: Math.max(1, Math.round(dayTotal / 500000)),
      pedidos: Math.max(1, Math.round(webAmount / 150000)),
    };
  });
}

/**
 * Comparativa porcentual de ventas de hoy (Caja + Web) contra ayer. Sin ventas
 * ayer: 100% al alza si hoy hubo ventas, y sin badge si ambos días están en 0.
 */
function buildComparativaVentas(
  totalHoy: number,
  totalAyer: number,
): DashboardStats["comparativaVentas"] {
  if (totalAyer > 0) {
    const porcentaje =
      Math.round(((totalHoy - totalAyer) / totalAyer) * 100) || 0;
    return {
      porcentaje,
      tendencia: porcentaje > 0 ? "up" : porcentaje < 0 ? "down" : "neutral",
    };
  }
  return totalHoy > 0 ? { porcentaje: 100, tendencia: "up" } : undefined;
}

export function computeLocalDashboardStats(): DashboardStats {
  const ahora = new Date();
  const hoy = startOfDay(ahora);
  const ventas = ventasActivas(readVentasLocal());
  const pedidos = pedidosActivos(readPedidosLocal()).filter((p) =>
    Boolean(p.fechaCreacion),
  );
  const ventasHoy = ventas.filter((v) => isSameDay(ventaFecha(v), hoy));
  const pedidosHoy = pedidos.filter((p) => isSameDay(pedidoFecha(p), hoy));
  const totalHoy = ventasHoy.reduce((s, v) => s + (v.total || 0), 0);
  const cantidad = ventasHoy.length;
  const tiendaHoy = pedidosHoy.reduce((s, p) => s + (p.total || 0), 0);
  const totalDia = totalHoy + tiendaHoy;
  const transaccionesHoy = cantidad + pedidosHoy.length;
  const ventasSemanaBase = buildLast7DaysSeries(ventas, pedidos, hoy);
  const ventasSemana = hasUserRealCajaActivity()
    ? ventasSemanaBase
    : fillDemoPreviousDays(ventasSemanaBase);
  const ayerPoint = ventasSemana[ventasSemana.length - 2];
  const totalAyer = (ayerPoint?.total ?? 0) + (ayerPoint?.tienda ?? 0);
  const transaccionesAyer = ayerPoint?.cantidad ?? 0;

  /** Ticket promedio de los 7 días previos (Caja + Web). */
  const inicioSemanaAnterior = subDays(hoy, 7);
  const ventasSemanaAnterior = ventas.filter((v) => {
    const fecha = ventaFecha(v);
    return fecha >= inicioSemanaAnterior && fecha < hoy;
  });
  const pedidosSemanaAnterior = pedidos.filter((p) => {
    const fecha = pedidoFecha(p);
    return fecha >= inicioSemanaAnterior && fecha < hoy;
  });
  const transaccionesSemanaAnterior =
    ventasSemanaAnterior.length + pedidosSemanaAnterior.length;
  const totalSemanaAnterior =
    ventasSemanaAnterior.reduce((s, v) => s + (v.total || 0), 0) +
    pedidosSemanaAnterior.reduce((s, p) => s + (p.total || 0), 0);

  /** Ritmo del día: transacciones entre la primera venta y la hora actual. */
  const horasTransaccion = [
    ...ventasHoy.map((v) => getHours(ventaFecha(v))),
    ...pedidosHoy.map((p) => getHours(pedidoFecha(p))),
  ];
  const primeraHora = horasTransaccion.length
    ? Math.min(...horasTransaccion)
    : null;
  const horasActivas =
    primeraHora !== null ? Math.max(1, ahora.getHours() - primeraHora + 1) : 1;

  const productos = txRead("inventory_products", defaultProducts) as Array<{
    id?: string;
    nombre?: string;
    stock?: number;
    stockMinimo?: number;
    isActive?: boolean;
  }>;
  const stockBajoList = productos.filter(
    (p) =>
      p.isActive !== false &&
      Number(p.stock ?? 0) <= Number(p.stockMinimo ?? 5),
  );
  /** Unidades vendidas hoy por producto, para estimar el agotamiento de hoy. */
  const vendidasHoy = new Map<string, number>();
  ventasHoy.forEach((v) => {
    (v.productos ?? []).forEach((linea) => {
      if (!linea.productoId) return;
      vendidasHoy.set(
        linea.productoId,
        (vendidasHoy.get(linea.productoId) ?? 0) +
          (Number(linea.cantidad) || 1),
      );
    });
  });
  const stockRiesgoHoy = stockBajoList.filter((p) => {
    const stock = Number(p.stock ?? 0);
    const vendidas = p.id ? (vendidasHoy.get(p.id) ?? 0) : 0;
    return stock === 0 || (vendidas > 0 && vendidas >= stock);
  });
  const stockPrincipales = [...stockBajoList]
    .sort((a, b) => Number(a.stock ?? 0) - Number(b.stock ?? 0))
    .slice(0, 3)
    .map((p) => ({
      nombre: p.nombre ?? p.id ?? "Producto",
      stock: Number(p.stock ?? 0),
      stockMinimo: Number(p.stockMinimo ?? 5),
    }));

  const empleados = txRead("personal_empleados", []) as Array<{
    isActive?: boolean;
    estado?: string;
  }>;
  const empleadosActivos = empleados.filter(
    (e) => e.isActive !== false && e.estado !== "inactivo",
  ).length;
  const empleadosTotal = empleados.length;

  return {
    ventasHoy: {
      total: totalHoy,
      tienda: tiendaHoy > 0 ? tiendaHoy : undefined,
      cantidad,
      promedio: cantidad > 0 ? Math.round(totalHoy / cantidad) : 0,
    },
    productosStockBajo: stockBajoList.length,
    empleadosActivos,
    empleadosTotal,
    comparativaVentas: buildComparativaVentas(totalDia, totalAyer),
    ventasSemana,
    transaccionesHoy,
    transaccionesAyer,
    ticketPromedioHoy:
      transaccionesHoy > 0 ? Math.round(totalDia / transaccionesHoy) : 0,
    ticketPromedioSemanaAnterior:
      transaccionesSemanaAnterior > 0
        ? Math.round(totalSemanaAnterior / transaccionesSemanaAnterior)
        : 0,
    ritmoPorHora:
      transaccionesHoy > 0 ? Math.round(transaccionesHoy / horasActivas) : 0,
    stockCritico: {
      riesgoHoy: stockRiesgoHoy.length,
      agotados: stockBajoList.filter((p) => Number(p.stock ?? 0) === 0).length,
      principales: stockPrincipales,
    },
  };
}

/**
 * Suma el total de pedidos de tienda online activos (no cancelados) creados hoy.
 * Permite mostrar el desglose Caja / Web en la tarjeta "Ventas Hoy" del Dashboard.
 */
export function computeVentasTiendaHoy(): number {
  const hoy = startOfDay(new Date());
  return pedidosActivos(readPedidosLocal())
    .filter((p) => {
      if (!p.fechaCreacion) return false;
      return isSameDay(new Date(p.fechaCreacion), hoy);
    })
    .reduce((s, p) => s + (p.total || 0), 0);
}

export function computeLocalDashboardKpiExtras(): DashboardKpiExtras {
  const today = startOfDay(new Date());
  const ventasHoy = ventasActivas(readVentasLocal()).filter((venta) =>
    isSameDay(ventaFecha(venta), today),
  );
  const pedidos = pedidosActivos(readPedidosLocal());
  const pedidosHoy = pedidos.filter((pedido) =>
    isSameDay(pedidoFecha(pedido), today),
  );
  const totalVentas = ventasHoy.reduce(
    (total, venta) => total + (venta.total || 0),
    0,
  );
  const totalPedidos = pedidosHoy.reduce(
    (total, pedido) => total + (pedido.total || 0),
    0,
  );
  const transacciones = ventasHoy.length + pedidosHoy.length;
  const peakHours = computeLocalPeakHours("hoy");
  const peakHour = peakHours.reduce<LocalPeakHourPoint | null>(
    (best, point) => (!best || point.total > best.total ? point : best),
    null,
  );
  const topProductos = computeLocalTopProducts(3);
  const topProduct = topProductos[0] ?? null;
  const equipo = [...computeLocalTeamPerformance("today")].sort(
    (first, second) => second.totalVentas - first.totalVentas,
  );
  const topVendedores = equipo.slice(0, 3);
  const bestSeller = topVendedores[0] ?? null;
  const secondSeller = topVendedores[1] ?? null;
  const totalEquipo = equipo.reduce((sum, item) => sum + item.totalVentas, 0);
  const clientes = txRead<Array<{ creadoEn?: string }>>(
    "fidelizacion_clientes",
    [],
  );
  const clientesNuevos = Array.isArray(clientes)
    ? clientes.filter(
        (cliente) =>
          cliente.creadoEn && isSameDay(new Date(cliente.creadoEn), today),
      ).length
    : 0;
  const clientesNuevosSemana: SalesPoint[] = Array.from(
    { length: 7 },
    (_, i) => {
      const d = addDays(today, i - 6);
      const total = Array.isArray(clientes)
        ? clientes.filter(
            (cliente) =>
              cliente.creadoEn && isSameDay(new Date(cliente.creadoEn), d),
          ).length
        : 0;
      return { name: WEEKDAY_SHORT[weekdayIndex(d)] ?? "", total };
    },
  );
  const inicioMes = startOfMonth(today);
  const clientesNuevosMes = Array.isArray(clientes)
    ? clientes.filter((cliente) => {
        if (!cliente.creadoEn) return false;
        const fecha = new Date(cliente.creadoEn);
        return fecha >= inicioMes && fecha <= endOfDay(today);
      }).length
    : 0;
  const productosPorId = new Map(
    (
      txRead<Array<{ id?: string; costo?: number }>>(
        "inventory_products",
        [],
      ) ?? []
    ).map((producto) => [producto.id, Number(producto.costo ?? 0)]),
  );
  let ingresosConCosto = 0;
  let costosEstimados = 0;
  ventasHoy.forEach((venta) => {
    (venta.productos ?? []).forEach((producto) => {
      const cantidad = Number(producto.cantidad ?? 1);
      const precio = Number(producto.precioUnitario ?? 0);
      const costo = productosPorId.get(producto.productoId ?? "");
      if (costo === undefined || precio <= 0) return;
      ingresosConCosto += precio * cantidad;
      costosEstimados += costo * cantidad;
    });
  });
  const sessions = txRead<Record<string, unknown> | null>(
    "caja_sesiones_activas",
    null,
  );
  const hasOpenSession = Boolean(
    sessions && typeof sessions === "object" && Object.keys(sessions).length,
  );
  const cashAmount = Number(txRead("caja_monto", 0)) || 0;
  const completedOrderStates = new Set([
    "entregado",
    "completado",
    "cancelado",
    "cancelada",
  ]);
  const pedidosPendientes = pedidos.filter(
    (pedido) => !completedOrderStates.has((pedido.estado ?? "").toLowerCase()),
  ).length;
  /** Pedidos entregados/completados cuya última actualización fue hoy. */
  const pedidosDespachadosHoy = pedidos.filter((pedido) => {
    const estado = (pedido.estado ?? "").toLowerCase();
    if (estado !== "entregado" && estado !== "completado") return false;
    const iso = pedido.fechaActualizacion || pedido.fechaCreacion;
    return iso ? isSameDay(new Date(iso), today) : false;
  }).length;

  const tareas = txRead<
    Array<{
      estado?: string;
      completadaEn?: string;
      fechaCompletada?: string;
      updatedAt?: string;
    }>
  >("personal_tareas_v4", []);
  const tareasPendientes = tareas.filter(
    (t) => t.estado === "pendiente" || t.estado === "en_progreso",
  ).length;
  const tareasCompletadasHoy = tareas.filter((t) => {
    if (t.estado !== "completada") return false;
    const iso = t.completadaEn || t.fechaCompletada || t.updatedAt;
    return iso ? isSameDay(new Date(iso), today) : false;
  }).length;

  const ventasTodas = ventasActivas(readVentasLocal());
  const margenDe = (desde: Date, hasta: Date): number | null => {
    let ingresos = 0;
    let costos = 0;
    ventasTodas.forEach((venta) => {
      const fecha = ventaFecha(venta);
      if (fecha < desde || fecha > hasta) return;
      (venta.productos ?? []).forEach((producto) => {
        const cantidad = Number(producto.cantidad ?? 1);
        const precio = Number(producto.precioUnitario ?? 0);
        const costo = productosPorId.get(producto.productoId ?? "");
        if (costo === undefined || precio <= 0) return;
        ingresos += precio * cantidad;
        costos += costo * cantidad;
      });
    });
    return ingresos > 0
      ? Math.round(((ingresos - costos) / ingresos) * 100)
      : null;
  };
  const margenSemana: SalesPoint[] = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(today, i - 6);
    return {
      name: WEEKDAY_SHORT[weekdayIndex(d)] ?? "",
      total: margenDe(startOfDay(d), endOfDay(d)) ?? 0,
    };
  });
  const margenMesPromedio = margenDe(inicioMes, endOfDay(today));

  return {
    ticketPromedio:
      transacciones > 0
        ? Math.round((totalVentas + totalPedidos) / transacciones)
        : 0,
    transacciones,
    horaPico:
      peakHour && peakHour.total > 0
        ? { label: peakHour.fullName, total: peakHour.total }
        : null,
    productoEstrella: topProduct,
    topProductos,
    mejorVendedor: bestSeller,
    segundoVendedor: secondSeller,
    topVendedores,
    totalEquipo,
    clientesNuevos,
    margenBrutoEstimado:
      ingresosConCosto > 0
        ? Math.round(
            ((ingresosConCosto - costosEstimados) / ingresosConCosto) * 100,
          )
        : null,
    efectivoEnCaja: hasOpenSession ? cashAmount : null,
    pedidosPendientes,
    tareasHoy: {
      completadas: tareasCompletadasHoy,
      total: tareasPendientes + tareasCompletadasHoy,
    },
    pedidosHoy: {
      despachados: pedidosDespachadosHoy,
      total: pedidosPendientes + pedidosDespachadosHoy,
    },
    monthlyTransactions: computeLocalMonthlyTransactions(),
    clientesNuevosSemana,
    clientesNuevosMes,
    margenSemana,
    margenMesPromedio,
  };
}

/**
 * Retorna true SOLO cuando el usuario ha registrado al menos una venta real en caja.
 * Usa hasUserRealCajaActivity() para excluir las ventas demo seeded automáticamente.
 * Esto garantiza que Dashboard y Métricas muestren datos hook demo correctamente
 * hasta que el usuario opere caja por primera vez.
 */
export function hasLocalVentasData(): boolean {
  return hasUserRealCajaActivity();
}

/**
 * Rango de años con datos del negocio (caja + tienda virtual).
 * Controla los niveles "Décadas" y "Año" del gráfico de tendencia:
 * - Con actividad real: min/max año de las ventas/pedidos.
 * - Modo demo (sin actividad real): simula historial 2000 → año actual para
 *   que el selector de décadas tenga sentido en la vista previa.
 * - Sin ventas ni pedidos: null (sin historial → sin décadas).
 */
export function computeChartYearRange(): ChartYearRange | null {
  if (!hasUserRealCajaActivity()) {
    return { minYear: 2000, maxYear: new Date().getFullYear() };
  }

  const years = new Set<number>();
  ventasActivas(readVentasLocal()).forEach((v) => {
    years.add(ventaFecha(v).getFullYear());
  });
  pedidosActivos(readPedidosLocal()).forEach((p) => {
    years.add(pedidoFecha(p).getFullYear());
  });
  if (years.size === 0) return null;

  const thisYear = new Date().getFullYear();
  return {
    minYear: Math.min(...years),
    maxYear: Math.max(thisYear, ...years),
  };
}

/** Suma total de ventas activas de ayer */
export function computeVentasYesterday(): number {
  const ayer = subDays(startOfDay(new Date()), 1);
  return ventasActivas(readVentasLocal())
    .filter((v) => isSameDay(ventaFecha(v), ayer))
    .reduce((s, v) => s + (v.total || 0), 0);
}

/** Meta base fija del negocio (mínima). */
const META_BASE_DIA = 1_000_000;

/**
 * Calcula la meta del día: max($1.000.000, mejor día histórico anterior).
 * Implementa el sistema recórd-tras-recórd: cada vez que se supera la meta,
 * el nuevo umbral a batir mañana es lo que se vendió hoy.
 * Excluye el día actual para que el reto no suba en tiempo real.
 */
export function computeMetaDia(): number {
  const hoy = startOfDay(new Date());
  const ventas = ventasActivas(readVentasLocal());

  // Agrupar totales por día (solo días previos)
  const totalByDay: Record<string, number> = {};
  ventas
    .filter((v) => !isSameDay(ventaFecha(v), hoy))
    .forEach((v) => {
      const key = format(ventaFecha(v), "yyyy-MM-dd");
      totalByDay[key] = (totalByDay[key] ?? 0) + (v.total || 0);
    });

  const values = Object.values(totalByDay);
  const maxPreviousDay = values.length > 0 ? Math.max(...values) : 0;
  return Math.max(META_BASE_DIA, maxPreviousDay);
}

/** Cuenta tareas pendientes o en_progreso en localStorage */
export function computeLocalTareasPendientes(): number {
  const tareas = txRead("personal_tareas_v4", []) as Array<{
    estado?: string;
  }>;
  return tareas.filter(
    (t) => t.estado === "pendiente" || t.estado === "en_progreso",
  ).length;
}
